import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { WCardTemplatePhotoPreviewView } from '@zola/svc-web-api-ts-client';
import { getPLPPhotoPreviewState } from 'cards/selectors/plpPhotoPreview';
import { getImageUrl } from 'cards/util/imageHelper';
import FeatureFlags from 'util/featureFlags';
import { SUPPORTED_CARD_TYPES } from '../constants';
import { Box, Layers, PhotoLayer, TransparentLayer } from './styles';

export interface PreviewProps {
  cardType: string;
  template?: WCardTemplatePhotoPreviewView;
  altText?: string;
  Fallback: () => JSX.Element | null;
}

const Preview = ({ cardType, template, altText, Fallback }: PreviewProps): JSX.Element | null => {
  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);
  const [transparentBox, setTransparentBox] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [photoBoxes, setPhotoBoxes] = useState<
    {
      width: number;
      height: number;
      x: number;
      y: number;
    }[]
  >([]);

  const measureRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null && template?.card_dimensions) {
        const boundingBox = node.getBoundingClientRect();
        const { width = 1, height = 1 } = template.card_dimensions;

        // landscape
        if (width > height) {
          const boxWidth = boundingBox.width - 5;
          const boxHeight = (height / width) * boxWidth;
          setTransparentBox({ width: boxWidth, height: boxHeight });
        } else {
          const boxHeight = boundingBox.height - 5;
          const boxWidth = (width / height) * boxHeight;
          setTransparentBox({ width: boxWidth, height: boxHeight });
        }
      }
    },
    [template?.card_dimensions]
  );

  useEffect(() => {
    if (template?.card_dimensions && transparentBox && template.slots) {
      const { width: cardWidth = 1, height: cardHeight = 1 } = template.card_dimensions;

      const newPhotoBoxes = template.slots.map(slot => {
        const { x: slotX = 0, y: slotY = 0, width: slotWidth = 1, height: slotHeight = 1 } = slot;

        return {
          x: transparentBox.width * (slotX / cardWidth),
          y: transparentBox.height * (slotY / cardHeight),
          width: transparentBox.width * (slotWidth / cardWidth),
          height: transparentBox.height * (slotHeight / cardHeight),
        };
      });

      setPhotoBoxes(newPhotoBoxes);
    }
  }, [template, transparentBox]);

  if (
    !FeatureFlags.get('plpPhotoPreview') ||
    !SUPPORTED_CARD_TYPES.includes(cardType) ||
    !template ||
    !plpPhotoPreviewState?.uploadcareFile
  ) {
    return <Fallback />;
  }

  return (
    <Box ref={measureRef}>
      <Layers box={transparentBox}>
        {photoBoxes?.map((box, index) => (
          <PhotoLayer
            data-testid="live-preview-photo-layer"
            key={index}
            url={plpPhotoPreviewState.uploadcareFile.cdnUrl}
            box={box}
          />
        ))}
        <TransparentLayer
          box={transparentBox}
          src={getImageUrl(template.image_uuid)}
          alt={altText}
        />
      </Layers>
    </Box>
  );
};

export default Preview;
